import { Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { authStore } from './authStore';
import { consumeCrossLaunchToken } from './crossLaunchAuthApi';
import { resolveClientEnv } from '../graphql/config/resolveClientEnv';

const CROSS_LAUNCH_COPY = {
  failureHeading: '403 Unauthorized',
  failureMessage: "We couldn't verify your cross-launch link. Please sign in to continue.",
  signInAction: 'Go to sign in',
  signingIn: 'Signing you in…',
} as const;

const FALLBACK_PATH = '/';
const INTERNAL_REDIRECT_ORIGIN = 'https://field-ops.invalid';

type CrossLaunchStatus = 'pending' | 'failure';

// A cross-launch destination must remain an app-relative path. The URL-origin
// check also rejects slash-backslash forms that browsers normalize to a host.
export const getSafeCrossLaunchPath = (path: string | null): string => {
  if (!path || !path.startsWith('/') || path.startsWith('//')) return FALLBACK_PATH;

  try {
    return new URL(path, INTERNAL_REDIRECT_ORIGIN).origin === INTERNAL_REDIRECT_ORIGIN ? path : FALLBACK_PATH;
  } catch {
    return FALLBACK_PATH;
  }
};

export const CrossLaunchView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<CrossLaunchStatus>('pending');
  const attemptedRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (attemptedRef.current) return;

    attemptedRef.current = true;
    authStore.clearAuth();

    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    if (!token?.trim()) {
      setStatus('failure');
      return;
    }

    const destination = getSafeCrossLaunchPath(searchParams.get('path'));

    const redeemAndNavigate = async (): Promise<void> => {
      try {
        const auth = await consumeCrossLaunchToken(token, { env: resolveClientEnv() });
        if (!isMountedRef.current) return;

        authStore.setAuth(auth);
        navigate(destination, { replace: true });
      } catch {
        if (!isMountedRef.current) return;

        authStore.clearAuth();
        setStatus('failure');
      }
    };

    void redeemAndNavigate();
  }, [location, navigate]);

  if (status === 'failure') {
    return (
      <Box
        component="main"
        sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: '100vh', p: { xs: 2, sm: 3 } }}
      >
        <Paper component="section" aria-labelledby="cross-launch-failure-heading" elevation={3} sx={{ maxWidth: 480, p: { xs: 3, sm: 4 }, width: '100%' }}>
          <Stack spacing={3}>
            <Typography component="h1" id="cross-launch-failure-heading" variant="h4">
              {CROSS_LAUNCH_COPY.failureHeading}
            </Typography>
            <Typography>{CROSS_LAUNCH_COPY.failureMessage}</Typography>
            <Button fullWidth onClick={() => navigate(FALLBACK_PATH)} variant="contained">
              {CROSS_LAUNCH_COPY.signInAction}
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: '100vh', p: { xs: 2, sm: 3 } }}
    >
      <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack aria-live="polite" role="status" spacing={2} sx={{ alignItems: 'center' }}>
          <CircularProgress aria-describedby="cross-launch-signing-in" />
          <Typography id="cross-launch-signing-in">{CROSS_LAUNCH_COPY.signingIn}</Typography>
        </Stack>
      </Paper>
    </Box>
  );
};
